import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/joy';
import PublicDocViewerPage from './PublicDocViewerPage';
import { readOfflinePublicDocPreviewEnvelope } from '../utils/offlinePublicDocPreview';

/** New-tab view of the workspace public-doc preview. Reads local storage only. */
const OfflinePublicDocPreviewPage: React.FC = () => {
  const previewId = useMemo(() => new URLSearchParams(window.location.search).get('preview'), []);
  const [payload, setPayload] = useState<Record<string, any> | null>(null);
  const [phase, setPhase] = useState<'building' | 'ready' | 'missing' | 'too-large'>('building');

  useEffect(() => {
    let settled = false;
    const tick = () => {
      const envelope = readOfflinePublicDocPreviewEnvelope();
      if (!previewId) {
        if (envelope?.status === 'ready' && envelope.payload) {
          setPayload(envelope.payload);
          setPhase('ready');
        } else {
          setPhase('missing');
        }
        return true;
      }
      if (!envelope || envelope.previewId !== previewId) return false;
      if (envelope.status === 'ready' && envelope.payload) {
        setPayload(envelope.payload);
        setPhase('ready');
        return true;
      }
      if (envelope.status === 'too-large') {
        setPhase('too-large');
        return true;
      }
      return false;
    };

    if (tick()) return undefined;
    const onStorage = () => {
      if (tick()) settled = true;
    };
    window.addEventListener('storage', onStorage);
    const timer = window.setInterval(() => {
      if (tick()) window.clearInterval(timer);
    }, 250);
    const giveUp = window.setTimeout(() => {
      if (!settled) setPhase((current) => (current === 'building' ? 'missing' : current));
    }, 20000);
    return () => {
      settled = true;
      window.removeEventListener('storage', onStorage);
      window.clearInterval(timer);
      window.clearTimeout(giveUp);
    };
  }, [previewId]);

  if (phase === 'building') {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        <Typography level="h3" sx={{ mb: 1 }}>Building preview</Typography>
        <Typography>Reading the saved document on this computer. Nothing is stored on the server.</Typography>
      </Box>
    );
  }

  if (phase === 'too-large') {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        <Typography level="h3" sx={{ mb: 1 }}>Preview is too large for a new tab</Typography>
        <Typography>The same preview is open in the workspace. Nothing is stored on the server.</Typography>
      </Box>
    );
  }

  if (phase !== 'ready' || !payload) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        <Typography level="h3" sx={{ mb: 1 }}>No preview open</Typography>
        <Typography>
          Open Preview from a saved First Survey, Final Survey, or Invoice. Nothing is stored on the server.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          px: 2,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.surface',
        }}
      >
        <Typography level="title-sm">Public document preview</Typography>
        <Button size="sm" variant="plain" onClick={() => window.close()}>Close</Button>
      </Stack>
      <PublicDocViewerPage offlinePreview={payload} />
    </>
  );
};

export default OfflinePublicDocPreviewPage;
